import { useNavigate, Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, MapPin, RefreshCcw, FileText, Receipt } from "lucide-react";
import { SEO } from "../components/SEO";
import { ADVISOR_DESK } from "../lib/constants";

const GUIDE_LINKS = [
  { to: "/declaration-impots-frontalier-suisse", label: "Déclaration impôt frontalier Suisse : guide complet" },
  { to: "/declaration-impots-frontalier-geneve", label: "Déclaration impôts frontalier Genève" },
  { to: "/impot-frontalier-geneve", label: "Impôt à la source Genève : barèmes et calcul" },
  { to: "/tou-geneve", label: "TOU Genève : taxation ordinaire ultérieure" },
  { to: "/quasi-resident-geneve", label: "Quasi-résident Genève : règle des 90 %" },
  { to: "/rectification-impot-source-geneve", label: "Rectification impôt à la source / DRIS" },
  { to: "/case-8tk-frontalier-suisse", label: "Case 8TK : le crédit d'impôt du frontalier" },
  { to: "/3eme-pilier-frontalier", label: "3e pilier frontalier : déductibilité" },
];

const points = [
  "Déclaration fiscale française avec revenus suisses",
  "Déclaration fiscale suisse si nécessaire",
  "Rectification d'impôt à la source (TOU / quasi-résident)",
  "Déductions : frais de déplacement, frais de garde, assurance maladie",
  "Crédit immobilier, biens immobiliers et revenus locatifs",
  "Déclaration des comptes bancaires à l'étranger",
  "Constitution et vérification des justificatifs",
  "Optimisation fiscale légale de votre situation transfrontalière",
];

const steps = [
  { icon: MapPin, title: "Analyse de votre statut", desc: "Canton de travail, pays de résidence, imposition à la source ou au réel : nous déterminons précisément vos obligations." },
  { icon: RefreshCcw, title: "Rectification à la source", desc: "Nous vérifions si une rectification d'impôt à la source (Genève et autres cantons) peut vous être favorable." },
  { icon: FileText, title: "Déclarations des deux pays", desc: "Nous préparons vos déclarations suisse et française en cohérence, sans double imposition." },
  { icon: Receipt, title: "Suivi et justificatifs", desc: "Nous vous indiquons les documents utiles et assurons le suivi jusqu'à la taxation finale." },
];

export default function Frontaliers() {
  const navigate = useNavigate();

  return (
    <main data-testid="frontaliers-page" className="pt-[76px]">
      <SEO
        title="Fiscaliste frontalier Genève & Suisse : déclaration, impôt à la source | Fiscoconseils"
        description="Fiscaliste frontalier Suisse-France : déclaration fiscale frontalier, rectification impôt à la source Genève (DRIS), TOU, quasi-résident, formulaire 2047. Annemasse, Pays de Gex, Haute-Savoie, 100 % en ligne."
      />

      <section className="bg-[#F8F9FA] border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#6B7280] mb-4">Frontaliers Suisse-France</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#0A1128] leading-[1.05]">
              La fiscalité des frontaliers, enfin simple
            </h1>
            <p className="text-base md:text-lg text-[#4B5563] mt-6 leading-relaxed">
              Vous travaillez en Suisse et vivez en France ? Fiscoconseils vous accompagne dans toutes vos obligations fiscales : déclarations, rectifications d'impôt à la source, déductions, justificatifs et optimisation fiscale.
            </p>
            <button
              data-testid="frontaliers-devis-btn"
              onClick={() => navigate("/devis")}
              className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#0A1128] rounded-full px-8 py-4 hover:bg-[#1A2342] transition-colors duration-200"
            >
              Demander un devis frontalier <ArrowRight size={16} />
            </button>
          </div>
          <img src={ADVISOR_DESK} alt="Conseiller fiscal frontalier Fiscoconseils" loading="lazy" width="640" height="420" className="rounded-2xl object-cover w-full h-[420px] shadow-[0_8px_32px_rgba(10,17,40,0.12)]" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A1128] max-w-2xl">
          Notre méthode en 4 étapes
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {steps.map((s, i) => (
            <div key={s.title} data-testid={`frontalier-step-${i}`} className="bg-white border border-[#C4C8CC]/50 rounded-2xl p-8 shadow-[0_8px_32px_rgba(10,17,40,0.06)]">
              <span className="font-heading text-4xl text-[#C4C8CC]">0{i + 1}</span>
              <s.icon size={24} strokeWidth={1.5} className="text-[#0A1128] mt-4" />
              <h3 className="text-base font-bold text-[#0A1128] mt-4">{s.title}</h3>
              <p className="text-sm text-[#6B7280] mt-2 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#0A1128] py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white max-w-2xl">
            Ce que nous prenons en charge pour vous
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-5 mt-12 max-w-4xl">
            {points.map((p) => (
              <div key={p} className="flex items-start gap-3">
                <CheckCircle2 size={18} strokeWidth={1.75} className="text-[#C4C8CC] mt-0.5 shrink-0" />
                <p className="text-sm text-[#D1D5DB] leading-relaxed">{p}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#6B7280] mt-12 max-w-2xl leading-relaxed">
            Les informations présentées sont indicatives. Toute analyse finale doit être confirmée par un conseiller fiscal Fiscoconseils. Aucune économie d'impôt ne peut être garantie.
          </p>
        </div>
      </section>

      <section data-testid="frontaliers-guides" className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A1128] max-w-2xl">
          Nos guides pour frontaliers Genève et Suisse
        </h2>
        <p className="text-base text-[#4B5563] mt-4 max-w-3xl leading-relaxed">
          Fiscaliste frontalier pour Genève, Annemasse, Saint-Julien-en-Genevois, le Pays de Gex et toute la Haute-Savoie, Fiscoconseils publie des guides détaillés sur la déclaration fiscale du frontalier, l'impôt à la source genevois, la TOU, la DRIS et le statut de quasi-résident.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {GUIDE_LINKS.map((g) => (
            <Link key={g.to} to={g.to} data-testid={`frontaliers-guide-${g.to.slice(1)}`} className="group bg-white border border-[#C4C8CC]/50 rounded-xl p-5 hover:border-[#0A1128]/40 transition-colors duration-200">
              <p className="text-sm font-bold text-[#0A1128] leading-snug">{g.label}</p>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#6B7280] mt-3 group-hover:text-[#0A1128]">Lire le guide <ArrowRight size={12} /></span>
            </Link>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/guides-fiscaux-frontaliers" className="text-sm font-semibold text-[#0A1128] underline underline-offset-4">Tous les guides fiscaux frontaliers</Link>
          <span className="text-[#C4C8CC]">·</span>
          <Link to="/outils-frontaliers" className="text-sm font-semibold text-[#0A1128] underline underline-offset-4">Outils gratuits : simulateur quasi-résident, convertisseur CHF/EUR</Link>
        </div>
      </section>
    </main>
  );
}
